import { useState, useCallback, useRef, useEffect } from 'react';
import L from 'leaflet';
import { 
  fetchBoundaryData, 
  convertOsmToGeoJson, 
  createBoundaryUrl 
} from '../services/boundaryService';
import { 
  getGeoJsonBounds, 
  createSafeFitBounds 
} from '../utils/mapUtils';
import { MAP_DEFAULTS } from '../constants/mapConstants';

export const useMapData = (regionsContext) => {
  const { 
    municipalRegions = [], 
    getSitesByRegion = () => [], 
    selectedRegion = null, 
    setSelectedRegion = () => {} 
  } = regionsContext || {};

  const [sites, setSites] = useState([]);
  const [cityBoundary, setCityBoundary] = useState(null);
  const [geoJson, setGeoJson] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState(new Set());
  const [mapCenter, setMapCenter] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(MAP_DEFAULTS.zoom);
  const [mapInstance, setMapInstance] = useState(null);
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [isLoadingBoundary, setIsLoadingBoundary] = useState(false);
  
  const isChangingRegionRef = useRef(false);
  
  const safeFitBounds = useCallback(
    createSafeFitBounds(mapInstance), 
    [mapInstance]
  );

  // Calculate default center from available regions
  const getDefaultCenter = useCallback(() => {
    const regionsWithCoords = municipalRegions.filter(
      (r) =>
        r.municipal_region_id !== "all" &&
        r.municipal_region_center_latitude &&
        r.municipal_region_center_longitude
    );

    if (regionsWithCoords.length > 0) {
      const firstRegion = regionsWithCoords[0];
      return {
        lat: parseFloat(firstRegion.municipal_region_center_latitude),
        lng: parseFloat(firstRegion.municipal_region_center_longitude),
      };
    }

    return MAP_DEFAULTS.center;
  }, [municipalRegions]);

  // Load boundary for a single region
  const loadSingleRegionBoundary = async (region) => {
    const url = createBoundaryUrl(region.municipal_region_boundary);
    const cacheKey = `region-${region.municipal_region_boundary}`;
    
    const osmData = await fetchBoundaryData(url, cacheKey);
    return convertOsmToGeoJson(osmData);
  };

  // Handle region change
  const handleRegionChange = useCallback(
    async (region) => {
      if (!region || isChangingRegionRef.current) return;
      
      isChangingRegionRef.current = true;
      setShowRegionDropdown(false);
      setIsLoadingBoundary(true);

      // Always reset before new fetch
      setGeoJson(null);
      setCityBoundary(null);
      setSelectedSiteIds(new Set());
      setSelectedSite(null);

      try {
        if (region.municipal_region_id === "all") {
          // Handle "all" regions case
          await handleAllRegions();
        } else {
          // Handle individual region
          await handleSingleRegion(region);
        }

        setSites(getSitesByRegion(region.municipal_region_id));
      } catch (err) {
        console.error("Error handling region change:", err);
      } finally {
        isChangingRegionRef.current = false;
        setIsLoadingBoundary(false);
      }
    },
    [municipalRegions, getSitesByRegion, getDefaultCenter, mapInstance, safeFitBounds]
  );

  const handleAllRegions = async () => {
    const tempCenter = getDefaultCenter();
    setMapCenter(tempCenter);
    setZoomLevel(11);

    // Load boundaries sequentially
    const regionsToLoad = municipalRegions.filter(
      r => r.municipal_region_id !== 'all' && r.municipal_region_boundary
    );
    
    if (regionsToLoad.length === 0) {
      console.warn('No regions with boundaries found');
      return;
    }
    
    let combinedGeoJson = {
      type: "FeatureCollection",
      features: []
    };
    
    let allBounds = null;

    for (let i = 0; i < regionsToLoad.length; i++) {
      const currentRegion = regionsToLoad[i];
      
      try {
        setIsLoadingBoundary(`Loading ${currentRegion.municipal_region_name} (${i + 1}/${regionsToLoad.length})...`);
        
        const geoJsonData = await loadSingleRegionBoundary(currentRegion);
        
        if (geoJsonData && geoJsonData.features) {
          combinedGeoJson.features = [
            ...combinedGeoJson.features,
            ...geoJsonData.features
          ];
          
          setGeoJson({
            type: "FeatureCollection",
            features: [...combinedGeoJson.features]
          });
          
          const currentBounds = getGeoJsonBounds(geoJsonData);
          if (currentBounds) {
            if (!allBounds) {
              allBounds = {...currentBounds};
            } else {
              allBounds.minlat = Math.min(allBounds.minlat, currentBounds.minlat);
              allBounds.minlon = Math.min(allBounds.minlon, currentBounds.minlon);
              allBounds.maxlat = Math.max(allBounds.maxlat, currentBounds.maxlat);
              allBounds.maxlon = Math.max(allBounds.maxlon, currentBounds.maxlon);
            }
            
            if (mapInstance && mapInstance._loaded) {
              const leafletBounds = L.latLngBounds(
                [allBounds.minlat, allBounds.minlon],
                [allBounds.maxlat, allBounds.maxlon]
              );
              safeFitBounds(leafletBounds);
            }
          }
        }
        
        if (i < regionsToLoad.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (err) {
        console.error(`Error loading boundary for ${currentRegion.municipal_region_name}:`, err);
      }
    }

    if (allBounds) {
      const centerLat = (allBounds.minlat + allBounds.maxlat) / 2;
      const centerLon = (allBounds.minlon + allBounds.maxlon) / 2;
      setMapCenter({ lat: centerLat, lng: centerLon });
      setCityBoundary(allBounds);
    }
  };

  const handleSingleRegion = async (region) => {
    const lat = parseFloat(region.municipal_region_center_latitude);
    const lng = parseFloat(region.municipal_region_center_longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setZoomLevel(13);
    }

    if (region?.municipal_region_boundary) {
      try {
        const geoJsonData = await loadSingleRegionBoundary(region);
        
        setGeoJson(geoJsonData);

        const osmbounds = getGeoJsonBounds(geoJsonData);
        if (osmbounds) {
          setCityBoundary(osmbounds);

          if (mapInstance && mapInstance._loaded) {
            const leafletBounds = L.latLngBounds(
              [osmbounds.minlat, osmbounds.minlon],
              [osmbounds.maxlat, osmbounds.maxlon]
            );
            safeFitBounds(leafletBounds);
          }
        }
      } catch (err) {
        console.error("Error loading boundary for region:", err);
      }
    } else {
      setCityBoundary(null);
      setGeoJson(null);
    }
  };

  // Other handlers
  const handleCheckboxChange = useCallback((siteId, checked) => {
    setSelectedSiteIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(siteId);
      } else {
        newSet.delete(siteId);
      }
      return newSet;
    });
  }, []);

  const fitToSelected = useCallback(() => {
    if (mapInstance && mapInstance._container && selectedSiteIds.size > 0) {
      const selectedSites = sites.filter((s) =>
        selectedSiteIds.has(s.site_id)
      );
      const bounds = L.latLngBounds(
        selectedSites.map((s) => [
          parseFloat(s.site_latitude),
          parseFloat(s.site_longitude),
        ])
      );
      safeFitBounds(bounds);
    }
  }, [mapInstance, sites, selectedSiteIds, safeFitBounds]);

  const handleSiteClick = (site) => {
    setSelectedSite(site);
    const lat = parseFloat(site.site_latitude);
    const lng = parseFloat(site.site_longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setZoomLevel(20);
    }

    setSelectedSiteIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(site.site_id)) {
        newSet.delete(site.site_id);
      } else {
        newSet.add(site.site_id);
      }
      return newSet;
    });
  };

  const resetMap = () => {
    if (selectedRegion && selectedRegion.municipal_region_id !== "all") {
      const lat = parseFloat(selectedRegion.municipal_region_center_latitude);
      const lng = parseFloat(selectedRegion.municipal_region_center_longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        setZoomLevel(13);
      }
    } else {
      setMapCenter(getDefaultCenter());
      setZoomLevel(11);
    }
  };

  const handleMapReady = useCallback((map) => {
    setMapInstance(map);
    setMapReady(true);
  }, []);

  // Initialize or update map when selected region changes
  useEffect(() => {
    if (selectedRegion && !isChangingRegionRef.current) {
      handleRegionChange(selectedRegion);
    }
  }, [selectedRegion]);

  return {
    // State
    sites,
    cityBoundary,
    geoJson,
    selectedSite,
    selectedSiteIds,
    mapCenter,
    zoomLevel,
    mapInstance,
    showRegionDropdown,
    mapReady,
    isLoadingBoundary,
    
    // Setters
    setShowRegionDropdown,
    setSelectedRegion,
    setCityBoundary,
    setMapCenter,
    setGeoJson,
    
    // Handlers
    handleRegionChange,
    handleCheckboxChange,
    fitToSelected,
    handleSiteClick,
    resetMap,
    handleMapReady,
    
    // Utilities
    getDefaultCenter
  };
};